/**
 * Page header component with title and breadcrumbs.
 *
 * @module components/layout/PageHeader
 */

import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  action?: ReactNode;
  /** Compact mode: tighter spacing, smaller title, inline description */
  compact?: boolean;
}

/**
 * Page header with title, description, breadcrumbs, and optional action
 */
export function PageHeader({ title, description, breadcrumbs, action, compact }: PageHeaderProps) {
  return (
    <div className={compact ? 'mb-4' : 'mb-8'}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          className={`flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 ${compact ? 'mb-1' : 'mb-2'}`}
        >
          {breadcrumbs.map((item, index) => (
            <div key={index} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="h-4 w-4" />}
              {item.href ? (
                <Link
                  to={item.href}
                  className="hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-gray-900 dark:text-gray-200">{item.label}</span>
              )}
            </div>
          ))}
        </nav>
      )}

      {/* Title and action */}
      <div
        className={
          compact
            ? 'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'
            : 'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'
        }
      >
        <div className={compact ? 'flex items-center gap-3 min-w-0' : undefined}>
          <h1
            className={
              compact
                ? 'text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100'
                : 'text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100'
            }
          >
            {title}
          </h1>
          {description && compact && (
            <span className="text-sm text-gray-500 dark:text-gray-300 truncate hidden sm:inline">
              {description}
            </span>
          )}
          {description && !compact && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">{description}</p>
          )}
        </div>
        {action && <div className="shrink-0 self-end sm:self-auto">{action}</div>}
      </div>
    </div>
  );
}
